import { Fragment, useMemo } from 'react';

import { Badge, Button, Divider, Text } from '@thefarmersfront/kitchen-mobile';
import clsx from 'clsx';

import { DATE_FORMAT_WITH_DAY_OF_WEEK } from '~/constants';
import {
  TypeMonthlyWorkPlanDaily,
  TypeTimeLineItem,
  TypeTimeLineSafetyEducationCompletionData,
  isCheckIn,
  isCheckOut,
  isEarlyEndWork,
  isEndWork,
  isOverWorkEarly,
  isOverWorkLate,
  isPreRegisteredWork,
  isSafetyEducationCompletion,
  isStartWork,
} from '~/hooks';
import { TypeCodeState } from '~/types';
import { getFormattedDate } from '~/utils';

import styles from './TimeLine.module.scss';

/** WorkInfo 타입 정의 */
export type TypeWorkInfoSecionContent = {
  cluster: string;
  center: string;
  workPart: string;
  teamName?: string;
  workShift: string;
};

/** EmptyTimeLine Props */
type TypeEmptyTimeLineProps = {
  contents: React.ReactNode;
  rightElement?: React.ReactNode;
};

/** WorkInfoSection Props */
type TypeWorkInfoSectionProps = {
  workInfo: TypeWorkInfoSecionContent;
  workType?: TypeCodeState | null;
  showDivider?: boolean;
  /** 근무 등록 요청 정보 */
  workPlanRegistrationInfo?: TypeMonthlyWorkPlanDaily | null;
  rightElement?: React.ReactNode;
};

/** WorkTimeLine Props */
type TypeWorkTimeLineProps = {
  workInfo?: TypeWorkInfoSecionContent | null;
  timeLine: TypeTimeLineItem[];
  /** 근무 정보 구분선 여부 */
  showWorkInfoDivider?: boolean;
  /** 가장 최근 이력 강조 여부 */
  highlightCurrentItem?: boolean;
  /** 근무 유형 */
  workType?: TypeCodeState | null;
  /** 지각 시간 */
  lateMinutes?: number | null;
};

/** WorkPlanTimeLine Props */
type TypeWorkPlanTimeLineProps = {
  workInfo?: TypeWorkInfoSecionContent | null;
  /** 근무 등록 요청 정보 */
  workPlanRegistrationInfo?: TypeMonthlyWorkPlanDaily | null;
  /** 근무 유형 */
  workType?: TypeCodeState | null;
  /** 근무 등록 버튼 여부 */
  showRegisterWorkPlanButton?: boolean;
  onMoveToRegisterWorkPlan?: (workInfo?: Partial<TypeWorkInfoSecionContent> | null) => void;
};

type TypeWorkPlanRegistrationTimeLineProps = {
  workType?: TypeCodeState | null;
  workPlanRegistrationInfo?: TypeMonthlyWorkPlanDaily | null;
  showRegisterWorkPlanButton?: boolean;
  onMoveToRegisterWorkPlan?: (workInfo?: Partial<TypeWorkInfoSecionContent> | null) => void;
};

/** @description 근무이력이 없을 때 표시하는 컴포넌트 */
export const EmptyTimeLine = ({ contents, rightElement }: TypeEmptyTimeLineProps) => {
  return (
    <div className={styles['today-work-timeline-contents']}>
      {typeof contents === 'string' ? (
        <Text className={styles['empty-time-line-text']} size="large" color="secondary">
          {contents.split('\n').map((text: string) => (
            <Fragment key={text}>
              {text}
              <br />
            </Fragment>
          ))}
        </Text>
      ) : (
        contents
      )}
      {rightElement}
    </div>
  );
};

/** @description 근무 정보 표시 영역 공통 컴포넌트 */
export const WorkInfoSection = ({
  workInfo,
  workType = null,
  showDivider = true,
  workPlanRegistrationInfo = null,
  rightElement,
}: TypeWorkInfoSectionProps) => {
  const hasPendingWorkPlanRegistration = ['PENDING', 'REJECTED'].includes(workPlanRegistrationInfo?.status?.code ?? '');

  return (
    <div className={styles['today-work-timeline-work-info']}>
      <div className={styles['today-work-timeline-work-info-container']}>
        <div>
          <div className={styles['today-work-timeline-work-info-item']}>
            {[workInfo.cluster, workInfo.center, workInfo.workPart, workInfo.teamName].map((value, index) => {
              const keyOfValue = `${value}-${index}`;
              return (
                value && (
                  <Fragment key={keyOfValue}>
                    {index !== 0 && <Divider orientation="vertical" color="gray200" height={12} />}
                    <Text color={hasPendingWorkPlanRegistration ? 'disabled' : 'secondary'} size="large" element="span">
                      {value}
                    </Text>
                  </Fragment>
                )
              );
            })}
          </div>
          {workInfo.workShift && (
            <Text color={hasPendingWorkPlanRegistration ? 'disabled' : 'tertiary'} size="large">
              {workInfo.workShift}
            </Text>
          )}
          {workType?.code && workType.code !== '근무' && (
            <Badge
              className={styles['work-type-badge']}
              color="secondary"
              variant="light"
              value={workType?.text}
              shape="square"
              size="medium"
            />
          )}
        </div>
        {rightElement}
      </div>
      {showDivider && (
        <Divider orientation="horizontal" color="gray100" className={styles['today-work-timeline-work-info-divider']} />
      )}
    </div>
  );
};

/** @description 근무 등록 요청 현황 뱃지 */
export const WorkPlanRegistrationStatusBadge = ({ status }: { status: null | string }) => {
  if (status === null) {
    return null;
  }

  return (
    <Badge
      color={status === 'PENDING' ? 'success' : 'error'}
      variant="light"
      shape="square"
      size="small"
      value={status === 'PENDING' ? '승인 대기 중' : '근무 등록 반려'}
    />
  );
};

/** @description 실제 근무 이력을 표시하는 컴포넌트 */
export const WorkTimeLine = ({
  workInfo = null,
  timeLine,
  showWorkInfoDivider = true,
  highlightCurrentItem = false,
  workType = null,
  lateMinutes = 0,
}: TypeWorkTimeLineProps) => {
  if (timeLine.length === 0 || timeLine === null) return null;

  return (
    <>
      {workInfo && <WorkInfoSection workInfo={workInfo} workType={workType} showDivider={showWorkInfoDivider} />}
      <div className={clsx(styles['today-work-timeline-contents'], workInfo && styles['show-work-info'])}>
        <ul>
          {timeLine?.map(
            (item, index) =>
              !isPreRegisteredWork(item) && (
                <li className={styles['today-work-timeline-item']} key={`${item.type.code}-${index}`}>
                  <span
                    className={clsx(styles['timeline-icon'], index === 0 && highlightCurrentItem && styles['current'])}
                    aria-hidden
                  />
                  <Text
                    className={clsx(
                      styles['timeline-title-text'],
                      ['CHECK_IN', 'CHECK_OUT'].includes(item.type.code) &&
                        styles['timeline-title-text-with-work-process']
                    )}
                    color="secondary"
                    weight="semi-bold"
                    size="large"
                  >
                    {item.type.text}
                    {isStartWork(item) && `${Number(lateMinutes) > 0 ? ` (지각 ${lateMinutes}분)` : ''}`}
                  </Text>
                  {isStartWork(item) && <StartEndWorkTimelineItem eventAt={item.data.startWorkAt} />}
                  {isEndWork(item) && <StartEndWorkTimelineItem eventAt={item.data.endWorkAt} />}
                  {isEarlyEndWork(item) && <StartEndWorkTimelineItem eventAt={item.data.eventAt} />}
                  {isOverWorkEarly(item) && (
                    <OverWorkTimeLineItem eventAt={item.data.eventAt} minutes={item.data.minutes} />
                  )}
                  {isOverWorkLate(item) && (
                    <OverWorkTimeLineItem eventAt={item.data.eventAt} minutes={item.data.minutes} />
                  )}
                  {isCheckIn(item) && (
                    <CheckInOutTimelineItem
                      mainWorkProcess={item.data.mainWorkProcess}
                      middleWorkProcess={item.data.middleWorkProcess}
                      eventAt={item.data.checkInAt}
                    />
                  )}
                  {isCheckOut(item) && (
                    <CheckInOutTimelineItem
                      mainWorkProcess={item.data.mainWorkProcess}
                      middleWorkProcess={item.data.middleWorkProcess}
                      eventAt={item.data.checkOutAt}
                    />
                  )}
                  {isSafetyEducationCompletion(item) && <SafetyEducationCompletionTimelineItem data={item.data} />}
                </li>
              )
          )}
        </ul>
      </div>
    </>
  );
};

/** @description 근무 계획을 표시하는 컴포넌트 */
export const WorkPlanTimeLine = ({
  workInfo = null,
  workType = null,
  workPlanRegistrationInfo = null,
  showRegisterWorkPlanButton = false,
  onMoveToRegisterWorkPlan,
}: TypeWorkPlanTimeLineProps) => {
  const buttonConfig = useMemo(() => {
    if (!workPlanRegistrationInfo) {
      return {
        text: '근무 등록',
        type: 'REGISTER',
      };
    }

    return null;
  }, [workPlanRegistrationInfo]);

  if (!workInfo) return null;

  return (
    <>
      <WorkInfoSection
        workInfo={workInfo}
        workType={workType}
        workPlanRegistrationInfo={workPlanRegistrationInfo}
        showDivider={false}
        rightElement={
          showRegisterWorkPlanButton &&
          buttonConfig && (
            <Button size="small" color="secondary" onClick={() => onMoveToRegisterWorkPlan?.(workInfo)}>
              {buttonConfig.text}
            </Button>
          )
        }
      />
    </>
  );
};

/** @description 근무 등록 요청 정보 표시 컴포넌트 */
export const WorkPlanRegistrationTimeLine = ({
  workType = { code: '근무', text: '근무' },
  workPlanRegistrationInfo,
  showRegisterWorkPlanButton,
  onMoveToRegisterWorkPlan,
}: TypeWorkPlanRegistrationTimeLineProps) => {
  const workInfo = useMemo(() => {
    return {
      cluster: workPlanRegistrationInfo?.cluster?.text ?? '',
      center: workPlanRegistrationInfo?.center?.text ?? '',
      workPart: workPlanRegistrationInfo?.workPart?.text ?? '',
      workShift: workPlanRegistrationInfo?.workShift?.formattedText ?? '',
    };
  }, [workPlanRegistrationInfo]);

  const buttonConfig = useMemo(() => {
    if (workPlanRegistrationInfo?.status?.code === 'REJECTED') {
      return {
        text: '다시 요청',
        type: 'REQUEST_AGAIN',
      };
    }

    return null;
  }, [workPlanRegistrationInfo]);

  if (!workPlanRegistrationInfo) return null;

  return (
    <>
      <WorkInfoSection
        workInfo={workInfo}
        workType={workType}
        workPlanRegistrationInfo={workPlanRegistrationInfo}
        showDivider={false}
        rightElement={
          showRegisterWorkPlanButton &&
          buttonConfig && (
            <Button
              size="small"
              color="secondary"
              onClick={() => onMoveToRegisterWorkPlan?.({ ...workInfo, workShift: undefined })}
            >
              {buttonConfig.text}
            </Button>
          )
        }
      />
      {buttonConfig?.type === 'REQUEST_AGAIN' && workPlanRegistrationInfo?.decisionReason && (
        <WorkPlanRegistrationRejectedReason reason={workPlanRegistrationInfo.decisionReason ?? ''} />
      )}
    </>
  );
};

/** @description 출근/퇴근 이력 컴포넌트 */
const StartEndWorkTimelineItem = ({ eventAt }: { eventAt: string }) => {
  return (
    <div>
      <Text color="quaternary" size="large">
        {getFormattedDate({ date: eventAt, format: DATE_FORMAT_WITH_DAY_OF_WEEK, separator: '.' })}
      </Text>
    </div>
  );
};

/** @description 연장 근무 이력 컴포넌트 */
const OverWorkTimeLineItem = ({ eventAt, minutes }: { eventAt: string; minutes: number }) => {
  return (
    <div className={styles['over-work-time-line-item']}>
      <Text color="quaternary" size="large">
        {getFormattedDate({ date: eventAt, format: DATE_FORMAT_WITH_DAY_OF_WEEK, separator: '.' })}
      </Text>
      <Text color="secondary" size="large">
        {minutes}분
      </Text>
    </div>
  );
};

/** @description 공정 체크인/체크아웃 이력 컴포넌트 */
const CheckInOutTimelineItem = ({
  mainWorkProcess,
  middleWorkProcess,
  eventAt,
}: {
  mainWorkProcess: string;
  middleWorkProcess: string;
  eventAt: string;
}) => {
  return (
    <>
      <div className={styles['timeline-item-work-process']}>
        <Text color="tertiary" size="large" element="span">
          {mainWorkProcess}
        </Text>
        <Divider orientation="vertical" color="gray200" height={12} />
        <Text color="tertiary" size="large" element="span">
          {middleWorkProcess}
        </Text>
      </div>
      <div>
        <Text color="quaternary" size="large">
          {getFormattedDate({ date: eventAt, format: DATE_FORMAT_WITH_DAY_OF_WEEK, separator: '.' })}
        </Text>
      </div>
    </>
  );
};

/** @description 안전교육 수료 이력 컴포넌트 */
const SafetyEducationCompletionTimelineItem = ({
  data,
}: {
  data: TypeTimeLineSafetyEducationCompletionData['data'];
}) => {
  return (
    <div className={styles['safety-education-completion-timeline-item']}>
      <div>
        <Text color="tertiary" size="large">
          교육 완료일
        </Text>
        <Text color="success" size="large">
          {getFormattedDate({ date: data.completionDate, separator: '.' })}
        </Text>
      </div>
      <div>
        <Text color="tertiary" size="large">
          교육 만료일
        </Text>
        <Text color="tertiary" size="large">
          {getFormattedDate({ date: data.expirationDate, separator: '.' })}
        </Text>
      </div>
    </div>
  );
};

export const WorkPlanRegistrationRejectedReason = ({ reason }: { reason: string }) => {
  return (
    <div className={styles['work-plan-registration-rejected-reason']}>
      <Divider orientation="horizontal" color="gray100" className={styles['today-work-timeline-work-info-divider']} />
      <Text size="large">{reason}</Text>
    </div>
  );
};
